import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    CreateTeaGradeRequest,
    GradeKind,
    ListTeaGradesParams,
    PatchTeaGradeRequest,
} from '@/lib/api/tea-grade-api'
import { scentingGradeApi, teaGradeApi } from '@/lib/api/tea-grade-api'
import { m } from '@/lib/paraglide/messages'

// Chọn qua hai export thay vì gradeApiFor để test mock được từng danh mục.
function apiFor(kind: GradeKind) {
    return kind === 'scenting' ? scentingGradeApi : teaGradeApi
}

// Key gốc tách theo danh mục: sau M-39 hai bảng có thể chung id, dùng chung
// key thì cache danh mục này ghi đè danh mục kia.
export function gradeKeys(kind: GradeKind) {
    const all = [
        kind === 'scenting' ? 'scenting-grades' : 'tea-grades',
    ] as const
    return {
        all,
        list: (params: ListTeaGradesParams | undefined) =>
            [...all, 'list', params ?? {}] as const,
        detail: (id: string) => [...all, 'detail', id] as const,
        stock: (id: string) => [...all, 'stock', id] as const,
        stockLots: (id: string) => [...all, 'stock-lots', id] as const,
    }
}

export const teaGradeKeys = gradeKeys('sorting')

export function useTeaGradesList(
    params?: ListTeaGradesParams,
    kind: GradeKind = 'sorting',
) {
    return useQuery({
        queryKey: gradeKeys(kind).list(params),
        queryFn: async () => {
            const res = await apiFor(kind).list(params)
            return res
        },
    })
}

export function useTeaGrade(
    id: string | undefined,
    kind: GradeKind = 'sorting',
) {
    const keys = gradeKeys(kind)
    return useQuery({
        queryKey: id ? keys.detail(id) : [...keys.all, 'detail', 'none'],
        enabled: !!id,
        queryFn: async () => {
            const res = await apiFor(kind).get(id ?? '')
            return res.data
        },
    })
}

/** Tồn theo kho của phân loại đang bung. id rỗng = chưa bung dòng nào → không
 *  gọi API. */
export function useTeaGradeStock(id: string, kind: GradeKind = 'sorting') {
    return useQuery({
        enabled: !!id,
        // Màn này để KIỂM tồn: phiếu nhập/xuất lô ở màn khác không làm mới key
        // này, nên mỗi lần bung tải lại (một request cho một dòng).
        staleTime: 0,
        queryKey: gradeKeys(kind).stock(id),
        queryFn: async () => {
            const res = await apiFor(kind).stock(id)
            return res.data ?? []
        },
    })
}

/** Từng lô còn tồn của phân loại đang bung, cho danh sách chi tiết theo kho.
 *  id rỗng = chưa bung dòng nào → không gọi API. */
export function useTeaGradeStockLots(id: string, kind: GradeKind = 'sorting') {
    return useQuery({
        enabled: !!id,
        // Giống useTeaGradeStock: màn này để KIỂM tồn nên mỗi lần bung tải lại,
        // phiếu nhập/xuất ở màn khác không làm mới key này.
        staleTime: 0,
        queryKey: gradeKeys(kind).stockLots(id),
        queryFn: async () => {
            const res = await apiFor(kind).stockLots(id)
            return res.data ?? []
        },
    })
}

export function useCreateTeaGrade(kind: GradeKind = 'sorting') {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: CreateTeaGradeRequest) => apiFor(kind).create(data),
        onSuccess: () => {
            toast.success(m.tea_grade_create_success())
            void qc.invalidateQueries({ queryKey: gradeKeys(kind).all })
        },
        onError: (err: unknown) => {
            const msg = (err as { message?: string })?.message
            toast.error(msg || m.tea_grade_create_error())
        },
    })
}

export function usePatchTeaGrade(kind: GradeKind = 'sorting') {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            id,
            data,
        }: {
            id: string
            data: PatchTeaGradeRequest
        }) => apiFor(kind).patch(id, data),
        onSuccess: () => {
            toast.success(m.tea_grade_update_success())
            void qc.invalidateQueries({ queryKey: gradeKeys(kind).all })
        },
        onError: (err: unknown) => {
            const msg = (err as { message?: string })?.message
            toast.error(msg || m.tea_grade_update_error())
        },
    })
}

export function useDeleteTeaGrade(kind: GradeKind = 'sorting') {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => apiFor(kind).remove(id),
        onSuccess: () => {
            toast.success(m.tea_grade_delete_success())
            void qc.invalidateQueries({ queryKey: gradeKeys(kind).all })
        },
        onError: (err: unknown) => {
            const msg = (err as { message?: string })?.message
            toast.error(msg || m.tea_grade_delete_error())
        },
    })
}
